/**
 * Utility functions for formatting numbers and financial data
 */

/**
 * Format large numbers with appropriate units (K, M, B, T)
 * @param value - Number or string to format
 * @returns Formatted string with $ prefix and appropriate unit
 */
export const formatFinancialNumber = (value: string | number): string => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;

  if (isNaN(num)) return value.toString();

  // Extract sign and work with absolute value
  const sign = num < 0 ? '-' : '';
  const absNum = Math.abs(num);

  if (absNum >= 1e12) {
    return `${sign}$${(absNum / 1e12).toFixed(1)}T`;
  } else if (absNum >= 1e9) {
    return `${sign}$${(absNum / 1e9).toFixed(1)}B`;
  } else if (absNum >= 1e6) {
    return `${sign}$${(absNum / 1e6).toFixed(1)}M`;
  } else if (absNum >= 1e3) {
    return `${sign}$${(absNum / 1e3).toFixed(1)}K`;
  }

  return `${sign}$${absNum.toLocaleString()}`;
};

/**
 * Format large numbers using compact units without a currency symbol.
 * Useful for quantities like share counts where only the magnitude matters.
 */
export const formatLargeNumber = (value: string | number): string => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;

  if (isNaN(num)) return value.toString();

  const sign = num < 0 ? '-' : '';
  const absNum = Math.abs(num);

  if (absNum >= 1e12) {
    return `${sign}${(absNum / 1e12).toFixed(1)}T`;
  } else if (absNum >= 1e9) {
    return `${sign}${(absNum / 1e9).toFixed(1)}B`;
  } else if (absNum >= 1e6) {
    return `${sign}${(absNum / 1e6).toFixed(1)}M`;
  } else if (absNum >= 1e3) {
    return `${sign}${(absNum / 1e3).toFixed(1)}K`;
  }

  return `${sign}${absNum.toLocaleString()}`;
};

/**
 * Format valuation ratios like P/E or P/S with a fixed decimal and suffix
 * @param value - Number or string to format
 * @param decimals - Number of decimal places to keep
 * @param suffix - Suffix appended to the formatted number (defaults to "x")
 */
export const formatRatio = (
  value: string | number,
  decimals?: number,
  suffix = 'x'
): string => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;

  if (isNaN(num)) return value.toString();

  // If decimals not specified, use dynamic: 0 for |num| >= 10, 1 for single digits
  const effectiveDecimals = decimals ?? (Math.abs(num) >= 10 ? 0 : 1);

  return `${num.toFixed(effectiveDecimals)}${suffix}`;
};

/**
 * Format earnings-per-share values with a currency symbol and proper sign placement
 * @param value - Number or string to format
 * @param decimals - Number of decimal places to keep
 */
export const formatEarningsPerShare = (
  value: string | number,
  decimals = 2
): string => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;

  if (isNaN(num)) return value.toString();

  const prefix = num < 0 ? '-$' : '$';
  const absoluteValue = Math.abs(num);

  return `${prefix}${absoluteValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`;
};

/**
 * Format employee count with appropriate units
 * @param count - Number or string representing employee count
 * @returns Formatted string with employee count and units
 */
export const formatEmployeeCount = (count: string | number): string => {
  const num = typeof count === 'string' ? parseInt(count.replace(/[^\d]/g, '')) : count;

  if (isNaN(num)) return count.toString();

  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M employees`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(0)}K employees`;
  }

  return `${num.toLocaleString()} employees`;
};

/**
 * Format percentage values with proper sign and suffix
 * @param value - Number or string to format as percentage
 * @param decimals - Number of decimal places to keep
 * @param includeSign - Whether to include + sign for positive values
 * @returns Formatted string with % suffix
 */
export const formatPercentage = (
  value: string | number,
  decimals = 1,
  includeSign = false
): string => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;

  if (isNaN(num)) return value.toString();

  const sign = includeSign && num > 0 ? '+' : '';
  return `${sign}${num.toFixed(decimals)}%`;
};

/**
 * Format dividend yield as percentage
 * @param value - Number or string representing dividend yield (already as percentage, e.g., 2.5 for 2.5%)
 * @param decimals - Number of decimal places to keep
 * @returns Formatted string with % suffix
 */
export const formatYield = (
  value: string | number,
  decimals = 2
): string => {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;

  if (isNaN(num)) return value.toString();

  // Value is already a percentage, just add % suffix
  return `${num.toFixed(decimals)}%`;
};
